import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, Badge, Spinner, Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import axios from 'axios';
import { useAuth } from '../../../Provider/AuthProvider';
import moment from 'moment';

// Réutilisation des interfaces depuis AllExpeditionsPage
interface ExpeditionLine {
    id: number;
    product_id: number;
    ref: string;
    libelle: string;
    qty: number;
    qty_shipped: number;
    subprice: number;
    tva_tx: number;
    origin_line_id: number;
}

interface Expedition {
    id: number;
    ref: string;
    status: number;
    status_name: string;
    date_creation: string;
    date_valid: string;
    date_delivery: string;
    date_expedition: string;
    socid: number;
    company_name: string;
    code_client: string;
    order_id: number;
    order_ref: string;
    order_ref_customer: string;
    order_date: string;
    user_login: string;
    nb_lines: number;
    note_public: string;
    note_private: string;
    lines: ExpeditionLine[];
    orderinfo: {
        orderid: number;
        ref: string;
        ref_client: string;
        client_name: string;
        date_creation: string;
    };
    type: 'comptoir' | 'franchise';
}

// Interface pour la modal de détails
interface ExpeditionDetailsModalProps {
    isOpen: boolean;
    toggle: () => void;
    expedition: Expedition | null;
}

// Composant Modal pour afficher les détails
const ExpeditionDetailsModal: React.FC<ExpeditionDetailsModalProps> = ({
    isOpen,
    toggle,
    expedition
}) => {
    const getStatusBadge = (status: number) => {
        switch (status) {
            case 0: return <Badge color="secondary">Brouillon</Badge>;
            case 1: return <Badge color="success">Validée</Badge>;
            case 2: return <Badge color="info">Clôturée</Badge>;
            case -1: return <Badge color="danger">Annulée</Badge>;
            default: return <Badge color="secondary">Inconnu</Badge>;
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString || dateString === "") return "Non définie";
        return moment(dateString).format('DD/MM/YYYY HH:mm');
    };

    const getTypeBadge = (type: string) => {
        switch (type) {
            case 'comptoir': return <Badge color="primary">Comptoir</Badge>;
            case 'franchise': return <Badge color="warning">Franchise</Badge>;
            default: return null;
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>
                <i className="ri-truck-line me-2"></i>
                Détails de l'expédition
            </ModalHeader>
            <ModalBody>
                {expedition && (
                    <div className="expedition-details">
                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h4 className="mb-1">{expedition.ref}</h4>
                                    <div className="d-flex align-items-center gap-2">
                                        {getStatusBadge(expedition.status)}
                                        {getTypeBadge(expedition.type)}
                                        <span className="text-muted">
                                            Créée le {formatDate(expedition.date_creation)}
                                        </span>
                                    </div>
                                </div>
                                <div className="text-end">
                                    <div className="fw-bold">{expedition.company_name}</div>
                                    <small className="text-muted">Client: {expedition.code_client}</small>
                                </div>
                            </div>
                        </div>

                        <div className="mb-4">
                            <h6 className="border-bottom pb-2 mb-3">
                                <i className="ri-shopping-cart-2-line me-2"></i>
                                Commande associée
                            </h6>
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-2">
                                        <small className="text-muted">Référence commande</small>
                                        <div className="fw-semibold">{expedition.order_ref}</div>
                                    </div>
                                    <div className="mb-2">
                                        <small className="text-muted">Date commande</small>
                                        <div className="fw-semibold">{formatDate(expedition.order_date)}</div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-2">
                                        <small className="text-muted">Référence client</small>
                                        <div className="fw-semibold">{expedition.order_ref_customer || 'Non définie'}</div>
                                    </div>
                                    <div className="mb-2">
                                        <small className="text-muted">Type</small>
                                        <div className="fw-semibold">
                                            {expedition.type === 'comptoir' ? 'Comptoir' : 'Franchise'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mb-4">
                            <h6 className="border-bottom pb-2 mb-3">
                                <i className="ri-list-check-2 me-2"></i>
                                Articles expédiés ({expedition.nb_lines} ligne(s))
                            </h6>
                            <div className="table-responsive">
                                <table className="table table-sm table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Référence</th>
                                            <th>Désignation</th>
                                            <th className="text-end">Quantité commandée</th>
                                            <th className="text-end">Expédiée</th>
                                            <th className="text-end">Prix Unitaire</th>
                                            <th className="text-end">TVA</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {expedition.lines && expedition.lines.length > 0 ? (
                                            expedition.lines.map((line) => (
                                                <tr key={line.id || Math.random()}>
                                                    <td>
                                                        <div className="fw-semibold">{line.ref || 'N/A'}</div>
                                                    </td>
                                                    <td>
                                                        <div className="text-truncate" style={{ maxWidth: '250px' }}>
                                                            {line.libelle || 'N/A'}
                                                        </div>
                                                    </td>
                                                    <td className="text-end">
                                                        <div className="fw-semibold">{line.qty || 0}</div>
                                                    </td>
                                                    <td className="text-end">
                                                        <Badge color={parseInt(line.qty_shipped?.toString() || "0") > 0 ? "success" : "warning"} className="fw-normal">
                                                            {line.qty_shipped || 0}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-end">
                                                        {line.subprice ? parseFloat(line.subprice.toString()).toFixed(2) + ' €' : '0.00 €'}
                                                    </td>
                                                    <td className="text-end">
                                                        {line.tva_tx || 0}%
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={6} className="text-center text-muted py-3">
                                                    Aucune ligne d'article trouvée pour cette expédition
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {expedition.note_public && (
                            <div className="mb-3">
                                <h6 className="border-bottom pb-2 mb-3">
                                    <i className="ri-sticky-note-line me-2"></i>
                                    Note publique
                                </h6>
                                <div className="p-3 bg-light rounded">
                                    {expedition.note_public}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                    Fermer
                </Button>
            </ModalFooter>
        </Modal>
    );
};

const RecentExpeditions = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [recentExpeditions, setRecentExpeditions] = useState<Expedition[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [loadingExpeditionId, setLoadingExpeditionId] = useState<number | null>(null);
    
    // États pour la modal de détails
    const [selectedExpedition, setSelectedExpedition] = useState<Expedition | null>(null);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);

    // Fonction pour obtenir le libellé et la couleur du statut
    const getStatusBadge = (status: number) => {
        switch (status) {
            case -1:
                return { label: "Annulée", color: "danger" };
            case 0:
                return { label: "Brouillon", color: "warning" };
            case 1:
                return { label: "Validée", color: "success" };
            case 2:
                return { label: "Clôturée", color: "primary" };
            default:
                return { label: `Statut ${status}`, color: "secondary" };
        }
    };

    // Helper function pour les noms de statut
    const getStatusName = (status: number): string => {
        switch (status) {
            case -1: return "Annulée";
            case 0: return "Brouillon";
            case 1: return "Validée";
            case 2: return "Clôturée";
            default: return "Inconnu";
        }
    };

    // Récupérer les expéditions récentes via l'API
    const fetchRecentExpeditions = async () => {
        if (!user?.token || !user?.entity) return;

        setLoading(true);
        setError(null);

        try {
            const response: any = await axios.post(
                "https://api.apapiecesauto.fr/apiV2/",
                {
                    action: "expeditions",
                    entity: user.entity,
                    page: 1,
                    limit: 10,
                    sort: '-date_creation'
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${user.token}`,
                    },
                }
            );

            // Gérer les différents formats de réponse
            let expeditionsData = [];
            
            if (Array.isArray(response.data)) {
                expeditionsData = response.data;
            } else if (response.data && Array.isArray(response.data.data)) {
                expeditionsData = response.data.data;
            } else {
                expeditionsData = [];
            }

            // Formatter les expéditions (adapté de votre fetchExpeditions)
            const formattedExpeditions = expeditionsData.slice(0, 5).map((exp: any) => {
                // Gestion des lignes
                let lines: ExpeditionLine[] = [];
                
                if (exp.lines && Array.isArray(exp.lines)) {
                    lines = exp.lines.map((line: any, lineIndex: number) => ({
                        id: Number(line.id) || Number(line.line_id) || lineIndex,
                        product_id: Number(line.product_id) || Number(line.fk_product) || 0,
                        ref: line.ref || line.product_ref || line.reference || '',
                        libelle: line.libelle || line.label || line.product_label || '',
                        qty: Number(line.qty) || Number(line.quantity) || 0,
                        qty_shipped: Number(line.qty_shipped) || Number(line.quantity_shipped) || 0,
                        subprice: Number(line.subprice) || Number(line.price) || 0,
                        tva_tx: Number(line.tva_tx) || Number(line.vat_rate) || 0,
                        origin_line_id: Number(line.origin_line_id) || Number(line.fk_origin_line) || 0,
                    }));
                }

                const status = Number(exp.status ?? exp.fk_statut ?? 0);
                const statusName = exp.status_name || getStatusName(status);

                return {
                    id: Number(exp.id) || 0,
                    ref: exp.ref || '',
                    status,
                    status_name: statusName,
                    date_creation: exp.date_creation || '',
                    date_valid: exp.date_valid || '',
                    date_delivery: exp.date_delivery || '',
                    date_expedition: exp.date_expedition || '',
                    socid: Number(exp.socid) || 0,
                    company_name: exp.company_name || '',
                    code_client: exp.code_client || '',
                    order_id: Number(exp.order_id) || 0,
                    order_ref: exp.order_ref || '',
                    order_ref_customer: exp.order_ref_customer || '',
                    order_date: exp.order_date || '',
                    user_login: exp.user_login || '',
                    nb_lines: Number(exp.nb_lines) || lines.length,
                    note_public: exp.note_public || '',
                    note_private: exp.note_private || '',
                    lines,
                    orderinfo: exp.orderinfo || {
                        orderid: Number(exp.order_id) || 0,
                        ref: exp.order_ref || '',
                        ref_client: exp.order_ref_customer || '',
                        client_name: exp.company_name || '',
                        date_creation: exp.order_date || ''
                    },
                    type: exp.type || ((exp.order_ref_customer && exp.order_ref_customer.trim() !== '') ? 'franchise' : 'comptoir')
                };
            });

            setRecentExpeditions(formattedExpeditions);
        } catch (err) {
            console.error("Erreur lors du chargement des expéditions récentes:", err);
            setError("Impossible de charger les expéditions récentes");
        } finally {
            setLoading(false);
        }
    };

    // Naviguer vers la page des expéditions avec l'expédition sélectionnée
    const handleViewExpedition = (expedition: Expedition) => {
        setLoadingExpeditionId(expedition.id);
        
        // Navigation vers la page des expéditions avec l'objet complet
        navigate('/expeditions', { 
            state: { 
                openExpeditionModal: true, 
                expeditionId: expedition.id,
                expeditionDetails: expedition // On passe l'objet complet
            } 
        });
        
        setLoadingExpeditionId(null);
    };

    // Nouvelle fonction pour ouvrir la modal de détails
    const openDetailsModal = (expedition: Expedition) => {
        setSelectedExpedition(expedition);
        setDetailsModalOpen(true);
    };

    // Charger les expéditions au montage du composant
    useEffect(() => {
        fetchRecentExpeditions();
    }, [user?.entity, user?.token]);

    // Rafraîchir les données
    const handleRefresh = () => {
        fetchRecentExpeditions();
    };

    // Naviguer vers toutes les expéditions
    const handleViewAllExpeditions = () => {
        navigate('/expeditions');
    };

    // Exporter le rapport
    const handleExport = () => {
        console.log("Export des expéditions récentes");
        // Logique d'export à implémenter
    };

    // Formatage de la date
    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        return moment(dateString).format('DD/MM/YYYY');
    };

    // Obtenir l'initiale du client
    const getClientInitial = (clientName: string) => {
        return clientName?.charAt(0).toUpperCase() || '?';
    };

    // Obtenir l'icône selon le type
    const getTypeIcon = (type: string) => {
        return type === 'franchise' ? 'ri-store-line' : 'ri-customer-service-line';
    };

    // Obtenir le libellé du type
    const getTypeLabel = (type: string) => {
        return type === 'franchise' ? 'Franchise' : 'Comptoir';
    };

    return (
        <>
            <Col xl={12}>
                <Card>
                    <CardHeader className="align-items-center d-flex">
                        <h4 className="card-title mb-0 flex-grow-1">
                            Dernières Expéditions
                            {loading && <Spinner size="sm" className="ms-2" />}
                        </h4>
                        <div className="flex-shrink-0">
                            <Button
                                type="button"
                                color="soft-secondary"
                                size="sm"
                                className="me-2"
                                onClick={handleRefresh}
                                disabled={loading}
                            >
                                <i className="ri-refresh-line align-middle"></i> Actualiser
                            </Button>
                            <Button
                                type="button"
                                color="soft-secondary"
                                size="sm"
                                onClick={handleExport}
                                disabled={loading || recentExpeditions.length === 0}
                            >
                                <i className="ri-file-list-3-line align-middle"></i> Exporter Rapport
                            </Button>
                        </div>
                    </CardHeader>

                    <CardBody>
                        {error && (
                            <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                <i className="ri-error-warning-line me-1"></i>
                                {error}
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setError(null)}
                                    aria-label="Close"
                                />
                            </div>
                        )}

                        {!loading && recentExpeditions.length === 0 && !error && (
                            <div className="text-center py-4">
                                <i className="ri-inbox-line fs-1 text-muted"></i>
                                <p className="text-muted mt-2">Aucune expédition récente</p>
                            </div>
                        )}

                        <div className="table-responsive table-card">
                            <table className="table table-borderless table-centered align-middle table-nowrap mb-0">
                                <thead className="text-muted table-light">
                                    <tr>
                                        <th scope="col">N° Expédition</th>
                                        <th scope="col">Client</th>
                                        <th scope="col">Commande</th>
                                        <th scope="col">Statut</th>
                                        <th scope="col">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentExpeditions.map((item) => {
                                        const status = getStatusBadge(item.status);
                                        
                                        return (
                                            <tr key={item.id}>
                                                <td>
                                                    <Button
                                                        color="link"
                                                        className="fw-medium text-reset p-0"
                                                        onClick={() => openDetailsModal(item)}
                                                        disabled={loadingExpeditionId === item.id}
                                                    >
                                                        
                                                        {item.ref}
                                                        {loadingExpeditionId === item.id && (
                                                            <Spinner size="sm" className="ms-1" />
                                                        )}
                                                    </Button>
                                                    
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div className="flex-shrink-0 me-2">
                                                            <div className="avatar-xs">
                                                                <div className="avatar-title bg-primary-subtle text-primary rounded-circle">
                                                                    {getClientInitial(item.company_name)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex-grow-1">
                                                            {item.company_name || 'Client inconnu'}
                                                            {item.code_client && (
                                                                <small className="d-block text-muted">
                                                                    Code: {item.code_client}
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                
                                                <td>
                                                    {item.order_ref ? (
                                                        <div>
                                                            <span className="fw-medium">{item.order_ref}</span>
                                                            {item.order_ref_customer && (
                                                                <small className="d-block text-muted">
                                                                    {item.order_ref_customer}
                                                                </small>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-1">
                                                        <Badge color={status.color}>
                                                            {status.label}
                                                        </Badge>
                                                       
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-1">
                                                        <Button
                                                            color="primary"
                                                            size="sm"
                                                            onClick={() => openDetailsModal(item)}
                                                            disabled={loadingExpeditionId === item.id}
                                                            title="Voir les détails"
                                                        >
                                                            <i className="ri-eye-line"></i>
                                                        </Button>
                                                        
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {recentExpeditions.length > 0 && (
                            <div className="text-center mt-3">
                                <Button
                                    color="link"
                                    onClick={handleViewAllExpeditions}
                                    className="text-primary"
                                >
                                    Voir toutes les expéditions
                                    <i className="ri-arrow-right-line ms-1"></i>
                                </Button>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Col>

            {/* Modal de détails */}
            <ExpeditionDetailsModal
                isOpen={detailsModalOpen}
                toggle={() => setDetailsModalOpen(false)}
                expedition={selectedExpedition}
            />
        </>
    );
};

export default RecentExpeditions;